import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './LeadDetail.css';
import { formatDateTime } from '../utils/dates';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = 'Bearer'; // Step 41 — auth via httpOnly cookie; this stub keeps existing headers harmless
const headers = { 'Authorization': API_KEY };
const jsonHeaders = { 'Authorization': API_KEY, 'Content-Type': 'application/json' };

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'viewing_arranged', label: 'Viewing Arranged' },
  { value: 'referencing_sent', label: 'Referencing Sent' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

// 2026-05-29 — Tri-state Yes/No control used in the Compliance Q&A
// section of ConvertModal. `value` is true | false | null (unanswered).
function YesNoField({ label, value, onChange, testid }) {
  const btn = (label, active, onClick, color) => (
    <button type="button"
            onClick={onClick}
            data-testid={`${testid}-${label.toLowerCase()}`}
            style={{
              flex: 1,
              padding: '0.4rem 0.75rem',
              border: '1px solid ' + (active ? color : '#cbd5e1'),
              borderRadius: 6,
              background: active ? color : '#fff',
              color: active ? '#fff' : '#0f172a',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}>
      {label}
    </button>
  );
  return (
    <div style={{ marginBottom: '0.65rem' }} data-testid={testid}>
      <div style={{ fontSize: '0.85rem', color: '#0f172a',
                    marginBottom: '0.3rem' }}>{label}</div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {btn('Yes', value === true, () => onChange(true), '#10b981')}
        {btn('No', value === false, () => onChange(false), '#ef4444')}
      </div>
    </div>
  );
}

const FIELD_LABELS = {
  full_name: 'Full Name',
  current_address: 'Current Address',
  rental_duration: 'Rental Duration',
  current_landlord: 'Current Landlord',
  reason_moving: 'Reason for Moving',
  move_in_date: 'Desired Move-in Date',
  occupants: 'Number of Occupants',
  employment_status: 'Employment Status',
  monthly_income: 'Approx. Monthly Income',
  pets: 'Pets',
  ccj: 'County Court Judgements',
  company_name: 'Company Name',
  property_address: 'Property Address',
  service_needed: 'Service Needed',
  trade: 'Trade / Company',
  property_job_ref: 'Property / Job Reference',
};

function formatDate(s) {
  return formatDateTime(s);
}

export default function LeadDetail() {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteChannel, setInviteChannel] = useState('whatsapp');
  const [inviteSubject, setInviteSubject] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastSubject, setBroadcastSubject] = useState('');

  const fetchLead = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}`, { headers });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Failed to load lead');
      setLead(data);
      setNotes(data.notes || '');
      setSelectedProp(data.matched_property_id || '');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [lead_id]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  useEffect(() => {
    // Load the agent's property list for the manual linker
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/properties/available`, { headers });
        if (res.ok) {
          const text = await res.text();
          setProperties(text ? JSON.parse(text) : []);
        }
      } catch {}
    })();
  }, []);

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}`, {
        method: 'PATCH', headers: jsonHeaders,
        body: JSON.stringify({ status: newStatus }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Failed to update');
      setLead(data);
    } catch (e) {
      alert('Status update failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}`, {
        method: 'PATCH', headers: jsonHeaders,
        body: JSON.stringify({ notes }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Failed to save');
      setLead(data);
    } catch (e) {
      alert('Failed to save notes: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const linkProperty = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}/link-property`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ property_id: selectedProp || null }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Failed to link');
      setLead(data);
    } catch (e) {
      alert('Link failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const openInvite = async (channel) => {
    setInviteChannel(channel);
    setInviteResult(null);
    setInviteLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}/invite-draft`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ channel }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Draft failed');
      setInviteSubject(data.subject || '');
      setInviteMessage(data.text_body || '');
      setInviteOpen(true);
    } catch (e) {
      alert(e.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const sendInvite = async () => {
    setInviteLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}/invite-to-view`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({
          channel: inviteChannel,
          subject: inviteChannel === 'email' ? inviteSubject : null,
          message: inviteMessage,
        }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Send failed');
      setInviteResult(data);
      // Refresh the lead so the new 'contacted' status shows
      fetchLead();
    } catch (e) {
      setInviteResult({ error: e.message });
    } finally {
      setInviteLoading(false);
    }
  };

  const openBroadcast = async () => {
    setBroadcastResult(null);
    // Seed the broadcast message from the standard WhatsApp draft
    setBroadcastLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}/invite-draft`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ channel: 'whatsapp' }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Draft failed');
      setBroadcastMessage(data.text_body || '');
      setBroadcastSubject((data.subject || '').replace('Viewing opportunity', 'Viewing opportunity'));
      setBroadcastOpen(true);
    } catch (e) {
      alert(e.message);
    } finally {
      setBroadcastLoading(false);
    }
  };

  const sendBroadcast = async () => {
    setBroadcastLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead_id}/invite-all-interested`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({
          subject: broadcastSubject || null,
          message: broadcastMessage,
        }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((data && data.detail) || 'Broadcast failed');
      setBroadcastResult(data);
      fetchLead();
    } catch (e) {
      setBroadcastResult({ error: e.message });
    } finally {
      setBroadcastLoading(false);
    }
  };

  if (loading) return <div className="lead-container"><div className="lead-loading">Loading lead...</div></div>;
  if (error || !lead) return (
    <div className="lead-container">
      <div className="lead-error">{error || 'Lead not found'}</div>
      <button className="btn-primary" onClick={() => navigate('/')}>Back to Dashboard</button>
    </div>
  );

  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Unknown Contact';
  const collected = lead.collected_fields || {};

  return (
    <div className="lead-container" data-testid="lead-detail-page">
      <div className="lead-back">
        <Link to="/" className="lead-back-link">&larr; Back to Dashboard</Link>
      </div>

      <header className="lead-header">
        <div>
          <h1 className="lead-title" data-testid="lead-name">{name}</h1>
          <div className="lead-meta">
            <span className={`lead-badge lead-badge-${lead.contact_type}`} data-testid="lead-type-badge">
              {lead.contact_type.replace('_', ' ')}
            </span>
            <span className={`lead-badge lead-source-${lead.source}`} data-testid="lead-source-badge">{lead.source}</span>
            <span className="lead-meta-dim">Received {formatDate(lead.created_at)}</span>
          </div>
        </div>
        <div className="lead-status-picker">
          <label htmlFor="lead-status-select">Status</label>
          <select id="lead-status-select"
                  value={lead.status}
                  disabled={saving}
                  onChange={e => updateStatus(e.target.value)}
                  data-testid="select-lead-status">
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {lead.matched_property && (lead.phone || lead.email) && (
            <div className="lead-invite-actions">
              {lead.phone && (
                <button type="button"
                        className="btn-view"
                        disabled={inviteLoading}
                        onClick={() => openInvite('whatsapp')}
                        data-testid="btn-invite-whatsapp">
                  Invite via WhatsApp
                </button>
              )}
              {lead.email && (
                <button type="button"
                        className="btn-view"
                        disabled={inviteLoading}
                        onClick={() => openInvite('email')}
                        data-testid="btn-invite-email">
                  Invite via Email
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="lead-grid">
        {/* Core contact details */}
        <section className="lead-card" data-testid="lead-contact-card">
          <h3>Contact Details</h3>
          <div className="lead-row"><span className="lead-label">Phone</span><span>{lead.phone || '—'}</span></div>
          <div className="lead-row"><span className="lead-label">Email</span><span>{lead.email || '—'}</span></div>
          <div className="lead-row"><span className="lead-label">Current Address</span><span>{lead.current_address || '—'}</span></div>
          <div className="lead-row"><span className="lead-label">Property of Interest</span><span>{lead.property_of_interest || '—'}</span></div>
        </section>

        {/* Matched property + manual linker */}
        <section className="lead-card" data-testid="lead-property-card">
          <h3>Matched Property</h3>
          {lead.matched_property ? (
            <>
              <div className="lead-row"><span className="lead-label">Address</span><span>{lead.matched_property.address}</span></div>
              <div className="lead-row">
                <span className="lead-label">Availability</span>
                <span className={`lead-badge pa-badge-${lead.matched_property.availability_status}`}>{lead.matched_property.availability_status.replace('_', ' ')}</span>
              </div>
              {lead.awaiting_follow_up && lead.awaiting_follow_up.length > 0 && (
                <div className="lead-awaiting" data-testid="awaiting-follow-up">
                  <div className="lead-awaiting-header">
                    <span className="lead-awaiting-title">
                      {lead.awaiting_follow_up.length} other {lead.awaiting_follow_up.length === 1 ? 'prospect' : 'prospects'} awaiting follow-up
                    </span>
                    <button className="btn-view"
                            disabled={broadcastLoading}
                            onClick={openBroadcast}
                            data-testid="btn-open-broadcast">
                      Invite all
                    </button>
                  </div>
                  <ul className="lead-awaiting-list">
                    {lead.awaiting_follow_up.slice(0, 5).map(item => (
                      <li key={item._id} data-testid={`awaiting-${item._id}`}>
                        <span className="lead-awaiting-name">{item.contact_name || 'Unknown'}</span>
                        <span className="lead-awaiting-channel">{item.contact_method}</span>
                        <span className="lead-awaiting-value">{item.contact_value}</span>
                      </li>
                    ))}
                    {lead.awaiting_follow_up.length > 5 && (
                      <li className="lead-awaiting-more">+ {lead.awaiting_follow_up.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <p className="lead-empty">No property linked. Link one below if this lead corresponds to a specific listing.</p>
          )}
          <div className="lead-link-form">
            <select value={selectedProp}
                    onChange={e => setSelectedProp(e.target.value)}
                    disabled={saving}
                    data-testid="select-link-property">
              <option value="">— Unlink —</option>
              {properties.map(p => (
                <option key={p.property_id} value={p.property_id}>
                  {p.address_line1}, {p.postcode}
                </option>
              ))}
            </select>
            <button className="btn-primary btn-sm" onClick={linkProperty} disabled={saving} data-testid="btn-link-property">
              {saving ? 'Saving...' : 'Apply'}
            </button>
          </div>
        </section>

        {/* Collected triage fields */}
        <section className="lead-card lead-card-wide" data-testid="lead-collected-card">
          <h3>Collected Fields</h3>
          {Object.keys(collected).filter(k => !k.startsWith('_')).length === 0 ? (
            <p className="lead-empty">No fields collected. This lead fell back from the triage to unknown.</p>
          ) : (
            <div className="lead-collected-grid">
              {Object.entries(collected)
                .filter(([k]) => !k.startsWith('_') && k !== 'matched_property_id' && k !== 'linked_maintenance_request_id' && k !== 'property_of_interest_raw')
                .map(([k, v]) => (
                  <div key={k} className="lead-collected-field">
                    <div className="lead-label">{FIELD_LABELS[k] || k.replace(/_/g, ' ')}</div>
                    <div className="lead-collected-value" data-testid={`collected-${k}`}>{String(v)}</div>
                  </div>
                ))}
            </div>
          )}
        </section>

        {/* Pre-Tenancy Checklist (Step 33).
            Moved ABOVE the Triage Conversation (2026-06-02) — agents
            asked to see AML/RTR/credit/reference status before reading
            the chat thread.
            2026-02 (Item 5) — Render the checklist for EVERY lead that
            isn't explicitly a landlord enquiry or contractor. Previously
            only leads with contact_type=='prospective_tenant' got the
            checklist, which left "unknown" leads (e.g. inbound emails
            where triage hadn't classified yet, or hand-created leads)
            with no AML / RTR / referencing UI. Letting agents almost
            always want the checklist on these — landlord/contractor
            enquiries are the only true negatives. */}
        {lead.contact_type !== 'landlord_enquiry'
          && lead.contact_type !== 'contractor' && (
          <PreTenancyChecklist
            lead={lead}
            onLeadRefresh={fetchLead}
          />
        )}

        {/* Conversation history — FIX 5 + 7 (2026-05-23) */}
        <section className="lead-card lead-card-wide" data-testid="lead-conversation-card">
          <h3>Triage Conversation</h3>
          {(() => {
            const interactions = Array.isArray(lead.interactions) ? lead.interactions : [];
            const tHistory = Array.isArray(lead.conversation_history) ? lead.conversation_history : [];
            if (interactions.length > 0) {
              return (
                <div className="lead-convo">
                  {interactions.map((it, i) => {
                    const isInbound = it.direction === 'inbound';
                    const roleLabel = isInbound
                      ? 'Contact'
                      : (it.auto_ack ? 'Estatel AI (auto-acknowledgement)' : 'Estatel AI AI');
                    const html = it.content_html
                      || (it.content_text
                          ? `<p>${String(it.content_text).replace(/\n\n/g,'</p><p>').replace(/\n/g,'<br>')}</p>`
                          : '');
                    return (
                      <div key={it._id || i}
                           className={`lead-msg lead-msg-${isInbound ? 'contact' : 'openclaw'}`}
                           data-testid={`convo-msg-${i}`}>
                        <div className="lead-msg-meta">
                          <span className="lead-msg-role">{roleLabel}</span>
                          {it.subject_line && (
                            <span className="lead-msg-subject" style={{marginLeft:'0.5rem',color:'#6b7280',fontStyle:'italic'}}>
                              {it.subject_line}
                            </span>
                          )}
                          <span className="lead-msg-time">{formatDate(it.created_at || it.sent_at)}</span>
                        </div>
                        <div
                          className="lead-msg-text lead-msg-html"
                          // FIX 7 — render HTML so paragraphs/bullets show; backend content
                          // comes only from the agent's own mailbox (auto-ack + AI triage
                          // replies), inbound from external senders is parsed by the
                          // IMAP poller. Treated as trusted given our pipeline.
                          dangerouslySetInnerHTML={{ __html: html }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            }
            // No Interactions yet — fall back to the in-memory triage history,
            // then to the saved enquiry_summary as a last resort.
            if (tHistory.length > 0) {
              return (
                <div className="lead-convo">
                  {tHistory.map((m, i) => (
                    <div key={i} className={`lead-msg lead-msg-${m.role}`} data-testid={`convo-msg-${i}`}>
                      <div className="lead-msg-meta">
                        <span className="lead-msg-role">{m.role === 'contact' ? 'Contact' : m.role === 'openclaw' ? 'Estatel AI AI' : 'Estatel AI AI (blocked)'}</span>
                        <span className="lead-msg-time">{formatDate(m.at)}</span>
                      </div>
                      <div
                        className="lead-msg-text lead-msg-html"
                        dangerouslySetInnerHTML={{
                          __html: `<p>${String(m.text || '').replace(/\n\n/g,'</p><p>').replace(/\n/g,'<br>')}</p>`
                        }}
                      />
                    </div>
                  ))}
                </div>
              );
            }
            if (lead.enquiry_summary) {
              return (
                <div className="lead-convo">
                  <div className="lead-msg lead-msg-contact" data-testid="convo-msg-original">
                    <div className="lead-msg-meta">
                      <span className="lead-msg-role">Original enquiry</span>
                      <span className="lead-msg-time">{formatDate(lead.received_at || lead.created_at)}</span>
                    </div>
                    <div
                      className="lead-msg-text lead-msg-html"
                      dangerouslySetInnerHTML={{
                        __html: `<p>${String(lead.enquiry_summary).replace(/\n\n/g,'</p><p>').replace(/\n/g,'<br>')}</p>`
                      }}
                    />
                  </div>
                </div>
              );
            }
            return <p className="lead-empty">No conversation recorded.</p>;
          })()}
        </section>

        {/* Pre-Tenancy Checklist moved above the conversation — see line ~424. */}

        {/* Notes */}
        <section className="lead-card lead-card-wide" data-testid="lead-notes-card">
          <h3>Agent Notes</h3>
          <textarea
            className="lead-notes"
            rows={4}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add internal notes, next steps, reminders..."
            data-testid="textarea-lead-notes"
          />
          <div className="lead-notes-actions">
            <button className="btn-primary btn-sm" onClick={saveNotes} disabled={saving} data-testid="btn-save-notes">
              {saving ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </section>
      </div>

      {inviteOpen && (
        <div className="lead-modal-backdrop" onClick={() => !inviteLoading && setInviteOpen(false)}>
          <div className="lead-modal" onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <div className="lead-modal-header">
              <h3>Invite to view — {inviteChannel === 'email' ? 'Email' : 'WhatsApp'}</h3>
              <button className="lead-modal-close" onClick={() => setInviteOpen(false)} data-testid="invite-close">×</button>
            </div>
            <div className="lead-modal-body">
              <div className="lead-row">
                <span className="lead-label">Recipient</span>
                <span data-testid="invite-recipient">
                  {inviteChannel === 'email' ? lead.email : lead.phone}
                </span>
              </div>
              {inviteChannel === 'email' && (
                <div className="lead-modal-field">
                  <label>Subject</label>
                  <input type="text"
                         value={inviteSubject}
                         onChange={e => setInviteSubject(e.target.value)}
                         data-testid="invite-subject" />
                </div>
              )}
              <div className="lead-modal-field">
                <label>Message</label>
                <textarea value={inviteMessage}
                          rows={10}
                          onChange={e => setInviteMessage(e.target.value)}
                          data-testid="invite-message" />
              </div>
              {inviteResult && (
                inviteResult.error ? (
                  <div className="invite-result invite-error" data-testid="invite-result">
                    Send failed: {inviteResult.error}
                  </div>
                ) : inviteResult.sent ? (
                  <div className="invite-result invite-ok" data-testid="invite-result">
                    Sent to {inviteResult.recipient} via {inviteResult.channel}. Lead status updated to contacted.
                  </div>
                ) : (
                  <div className="invite-result invite-warn" data-testid="invite-result">
                    Logged locally, but delivery could not be confirmed (check your channel configuration).
                  </div>
                )
              )}
            </div>
            <div className="lead-modal-footer">
              <button className="btn-secondary btn-sm"
                      onClick={() => setInviteOpen(false)}
                      disabled={inviteLoading}
                      data-testid="invite-cancel">
                Close
              </button>
              <button className="btn-view"
                      onClick={sendInvite}
                      disabled={inviteLoading || !inviteMessage.trim()}
                      data-testid="invite-send">
                {inviteLoading ? 'Sending...' : `Send via ${inviteChannel === 'email' ? 'Email' : 'WhatsApp'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {broadcastOpen && (
        <div className="lead-modal-backdrop" onClick={() => !broadcastLoading && setBroadcastOpen(false)}>
          <div className="lead-modal" onClick={e => e.stopPropagation()} data-testid="broadcast-modal">
            <div className="lead-modal-header">
              <h3>Invite all awaiting prospects</h3>
              <button className="lead-modal-close" onClick={() => setBroadcastOpen(false)} data-testid="broadcast-close">×</button>
            </div>
            <div className="lead-modal-body">
              <p className="lead-modal-hint">
                This will send the message below to all {lead.awaiting_follow_up?.length || 0} prospects who
                registered interest in this property and haven't been contacted yet.
                Each prospect will receive it via the channel they used originally (WhatsApp or email).
              </p>
              <div className="lead-modal-field">
                <label>Email subject (used for email recipients only)</label>
                <input type="text"
                       value={broadcastSubject}
                       onChange={e => setBroadcastSubject(e.target.value)}
                       data-testid="broadcast-subject" />
              </div>
              <div className="lead-modal-field">
                <label>Message</label>
                <textarea value={broadcastMessage}
                          rows={10}
                          onChange={e => setBroadcastMessage(e.target.value)}
                          data-testid="broadcast-message" />
              </div>
              {broadcastResult && (
                broadcastResult.error ? (
                  <div className="invite-result invite-error" data-testid="broadcast-result">
                    Broadcast failed: {broadcastResult.error}
                  </div>
                ) : (() => {
                  const total = (broadcastResult.sent || 0) + (broadcastResult.skipped || 0);
                  const unsupported = broadcastResult.skipped_unsupported || 0;
                  const failed = broadcastResult.failed_delivery || 0;
                  const parts = [`Sent to ${broadcastResult.sent} of ${total} prospects.`];
                  if (unsupported > 0) parts.push(`${unsupported} skipped — no supported channel on record.`);
                  if (failed > 0) parts.push(`${failed} failed to deliver — check channel configuration.`);
                  const cls = failed > 0 ? 'invite-warn' : 'invite-ok';
                  return (
                    <div className={`invite-result ${cls}`} data-testid="broadcast-result">
                      {parts.join(' ')}
                    </div>
                  );
                })()
              )}
            </div>
            <div className="lead-modal-footer">
              <button className="btn-secondary btn-sm"
                      onClick={() => setBroadcastOpen(false)}
                      disabled={broadcastLoading}
                      data-testid="broadcast-cancel">
                Close
              </button>
              <button className="btn-view"
                      onClick={sendBroadcast}
                      disabled={broadcastLoading || !broadcastMessage.trim()}
                      data-testid="broadcast-send">
                {broadcastLoading ? 'Sending...' : `Send to ${lead.awaiting_follow_up?.length || 0} prospects`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =====================================================================
 * Pre-Tenancy Checklist (Step 33)
 * Contains: 5-row checklist with required toggle / status / notes / doc
 * upload; Send Referencing Link button (with preview+confirm); Convert to
 * Tenant button (disabled until ready); Convert modal; Audit row shown
 * when the lead has already been converted.
 * ===================================================================== */

const CHECKLIST_META = [
  { key: 'credit_check', label: 'Credit Check' },
  { key: 'aml_check', label: 'AML Check' },
  { key: 'right_to_rent_check', label: 'Right to Rent Check' },
  { key: 'employer_reference', label: 'Employer Reference' },
  { key: 'previous_landlord_reference', label: 'Previous Landlord Reference' },
];

const STATUS_OPTS = [
  { value: 'not_required', label: 'Not required' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
];

function PreTenancyChecklist({ lead, onLeadRefresh }) {
  const [checklist, setChecklist] = useState(null);
  const [busy, setBusy] = useState(false);
  const [referencingOpen, setReferencingOpen] = useState(false);
  const [referencingSelectedKeys, setReferencingSelectedKeys] = useState([]);
  const [referencingPreview, setReferencingPreview] = useState(null);
  const [referencingMessage, setReferencingMessage] = useState('');
  const [referencingSubject, setReferencingSubject] = useState('');
  const [referencingResult, setReferencingResult] = useState(null);
  const [referencingLoading, setReferencingLoading] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [readiness, setReadiness] = useState({ ready: false, blocking: [] });
  const [allProps, setAllProps] = useState([]);
  const [suggestedPropId, setSuggestedPropId] = useState('');
  const [softCheckButtons, setSoftCheckButtons] = useState({});
  const [softCheckKey, setSoftCheckKey] = useState(null);

  const loadChecklist = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/checklist`, { headers });
      if (res.ok) {
        const t = await res.text();
        const d = t ? JSON.parse(t) : {};
        setChecklist(d.checklist || null);
        setSoftCheckButtons(d.soft_check_buttons || {});
      }
    } catch {}
  }, [lead._id]);

  const loadReadiness = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/convert-readiness`, { headers });
      if (res.ok) {
        const t = await res.text();
        setReadiness(t ? JSON.parse(t) : { ready: false, blocking: [] });
      }
    } catch {}
  }, [lead._id]);

  useEffect(() => { loadChecklist(); loadReadiness(); }, [loadChecklist, loadReadiness]);

  // Load the full property list (all statuses) so the agent can pick
  // any property for conversion — auto-assign is explicitly forbidden by spec.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/property-options`, { headers });
        if (res.ok) {
          const t = await res.text();
          const d = t ? JSON.parse(t) : {};
          setAllProps(d.properties || []);
          setSuggestedPropId(d.suggested_property_id || '');
        }
      } catch {}
    })();
  }, [lead._id]);

  const patchItem = async (key, patch) => {
    setBusy(true);
    try {
      await fetch(`${API_BASE_URL}/api/leads/${lead._id}/checklist/${key}`, {
        method: 'PATCH', headers: jsonHeaders,
        body: JSON.stringify(patch),
      });
      await loadChecklist();
      await loadReadiness();
    } finally { setBusy(false); }
  };

  const uploadDoc = async (key, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/checklist/${key}/upload`, {
        method: 'POST', headers, body: form,
      });
      if (!res.ok) {
        const t = await res.text();
        alert('Upload failed: ' + t);
      }
      await loadChecklist();
    } finally { setBusy(false); }
  };

  const openReferencing = async () => {
    // Default selected keys = all currently required items
    const required = CHECKLIST_META.filter(m => (checklist?.[m.key]?.required)).map(m => m.key);
    setReferencingSelectedKeys(required.length > 0 ? required : CHECKLIST_META.map(m => m.key));
    setReferencingResult(null);
    setReferencingPreview(null);
    setReferencingMessage('');
    setReferencingSubject('');
    setReferencingOpen(true);
  };

  const runPreview = async () => {
    setReferencingLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/referencing/preview`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ selected_keys: referencingSelectedKeys }),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) { alert(d.detail || 'Preview failed'); return; }
      setReferencingPreview(d);
      setReferencingMessage(d.message || '');
      setReferencingSubject(d.subject || '');
    } catch (e) {
      alert(e.message);
    } finally { setReferencingLoading(false); }
  };

  const confirmSend = async () => {
    setReferencingLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/referencing/send`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({
          selected_keys: referencingSelectedKeys,
          message: referencingMessage,
          subject: referencingSubject || null,
        }),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) { setReferencingResult({ error: d.detail }); return; }
      setReferencingResult(d);
      await loadChecklist();
      onLeadRefresh();
    } finally { setReferencingLoading(false); }
  };

  if (lead.converted_at) {
    const conv = lead.converted || {};
    return (
      <>
        <section className="lead-card lead-card-wide pretenancy-audit" data-testid="pretenancy-audit">
          <h3>Converted to Tenant</h3>
          <p className="pretenancy-audit-line" data-testid="converted-at">
            Converted on {formatDate(lead.converted_at)}.
          </p>
          <div className="pretenancy-audit-links">
            {conv.tenant && (
              <Link
                to={`/tenants/${conv.tenant._id}`}
                style={{ background: '#0073EA', color: '#fff', textDecoration: 'none' }}
                className="btn-sm"
                data-testid="link-converted-tenant">
                View Tenant — {conv.tenant.name}
              </Link>
            )}
            {conv.tenancy && (
              <Link
                to={`/tenants/${conv.tenant?._id || ''}?tab=tenancy`}
                style={{ background: '#8b5cf6', color: '#fff', textDecoration: 'none' }}
                className="btn-sm"
                data-testid="link-converted-tenancy">
                View Tenancy (start {conv.tenancy.start_date || '—'})
              </Link>
            )}
          </div>
        </section>

        {/* Spec: checklist results must remain visible post-conversion */}
        {checklist && (
          <section className="lead-card lead-card-wide pretenancy-card" data-testid="pretenancy-checklist-readonly">
            <div className="pretenancy-header">
              <h3>Pre-Tenancy Checklist (record)</h3>
            </div>
            <table className="pretenancy-table">
              <thead>
                <tr>
                  <th>Check</th><th>Required</th><th>Status</th><th>Notes</th><th>Documents</th>
                </tr>
              </thead>
              <tbody>
                {CHECKLIST_META.map(({ key: k, label }) => {
                  const item = checklist[k] || {};
                  return (
                    <tr key={k} data-testid={`rc-row-${k}`}>
                      <td><strong>{label}</strong></td>
                      <td>{item.required ? 'Yes' : 'No'}</td>
                      <td>
                        <span className={`status-badge status-${item.status || 'pending'}`}>
                          {(item.status || 'pending').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>{item.notes || '—'}</td>
                      <td>
                        {(item.documents || []).length === 0
                          ? '—'
                          : (item.documents || []).map(d => (
                              <a key={d.id} href={`${API_BASE_URL}/api/pre-tenancy/doc/${d.id}`}
                                 target="_blank" rel="noreferrer"
                                 className="pretenancy-doc-link"
                                 data-testid={`rc-doc-${d.id}`}>
                                {d.filename || 'document'}
                              </a>
                            ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}
      </>
    );
  }

  return (
    <section className="lead-card lead-card-wide pretenancy-card" data-testid="pretenancy-checklist">
      <div className="pretenancy-header">
        <h3>Pre-Tenancy Checklist</h3>
        <div className="pretenancy-actions">
          <button
            className="btn-secondary btn-sm"
            onClick={openReferencing}
            disabled={busy}
            data-testid="btn-send-referencing"
          >
            Send Referencing Link
          </button>
          <button
            className={`btn-convert btn-sm ${readiness.ready ? 'btn-convert-ready' : ''}`}
            onClick={() => setConvertOpen(true)}
            disabled={!readiness.ready || busy}
            title={!readiness.ready ? 'Resolve all required checks (pass or mark not required) to enable' : 'Convert this lead to a tenant'}
            data-testid="btn-convert-to-tenant"
          >
            Convert to Tenant
          </button>
        </div>
      </div>

      {!readiness.ready && (readiness.blocking || []).length > 0 && (
        <p className="pretenancy-blocking" data-testid="pretenancy-blocking">
          {readiness.blocking.length} required item(s) still outstanding.
        </p>
      )}

      <div className="pretenancy-table">
        <div className="pt-row pt-head">
          <div className="pt-col pt-col-name">Item</div>
          <div className="pt-col pt-col-required">Required</div>
          <div className="pt-col pt-col-status">Status</div>
          <div className="pt-col pt-col-notes">Notes</div>
          <div className="pt-col pt-col-docs">Document</div>
        </div>
        {CHECKLIST_META.map(meta => {
          const item = checklist?.[meta.key] || { required: true, status: 'pending', notes: '', documents: [] };
          const soft = item.soft_check || {};
          const softState = soft.state;
          const softBtnLabel = softCheckButtons[meta.key];
          return (
            <div key={meta.key} className="pt-row" data-testid={`pt-row-${meta.key}`}>
              <div className="pt-col pt-col-name">
                {meta.label}
                {softState === 'awaiting_response' && (
                  <div className="pt-soft-chip pt-soft-chip-awaiting" data-testid={`pt-soft-awaiting-${meta.key}`}>
                    Awaiting Response
                  </div>
                )}
                {softState === 'response_received' && (
                  <div className="pt-soft-chip pt-soft-chip-ready" data-testid={`pt-soft-ready-${meta.key}`}>
                    Response Received
                  </div>
                )}
                {softBtnLabel && item.required && (
                  <button
                    type="button"
                    onClick={() => setSoftCheckKey(meta.key)}
                    disabled={busy}
                    className="pt-soft-btn"
                    style={{ background: softState === 'response_received' ? '#0073EA' : '#eef2ff', color: softState === 'response_received' ? '#fff' : '#4338ca' }}
                    data-testid={`pt-soft-btn-${meta.key}`}
                  >
                    {softState === 'response_received' ? 'Review Response' :
                     softState === 'awaiting_response' ? 'Resend / Edit' :
                     softBtnLabel}
                  </button>
                )}
              </div>
              <div className="pt-col pt-col-required">
                <label className="pt-toggle">
                  <input
                    type="checkbox"
                    checked={!!item.required}
                    disabled={busy}
                    onChange={e => patchItem(meta.key, { required: e.target.checked })}
                    data-testid={`pt-required-${meta.key}`}
                  />
                  <span>{item.required ? 'Required' : 'Not required'}</span>
                </label>
              </div>
              <div className="pt-col pt-col-status">
                <select
                  value={item.status || 'pending'}
                  disabled={busy || !item.required}
                  onChange={e => patchItem(meta.key, { status: e.target.value })}
                  data-testid={`pt-status-${meta.key}`}
                >
                  {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="pt-col pt-col-notes">
                <input
                  type="text"
                  defaultValue={item.notes || ''}
                  placeholder="Add notes..."
                  onBlur={e => e.target.value !== (item.notes || '') && patchItem(meta.key, { notes: e.target.value })}
                  data-testid={`pt-notes-${meta.key}`}
                />
              </div>
              <div className="pt-col pt-col-docs">
                {(item.documents || []).map(d => (
                  <a key={d.id}
                     href={`${API_BASE_URL}/api/pre-tenancy/doc/${d.id}`}
                     target="_blank" rel="noreferrer"
                     className="pt-doc-link"
                     data-testid={`pt-doc-${d.id}`}>
                    {d.filename}
                  </a>
                ))}
                <label className="pt-upload">
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/jpg"
                    style={{display:'none'}}
                    onChange={e => uploadDoc(meta.key, e.target.files?.[0])}
                    data-testid={`pt-upload-${meta.key}`}
                  />
                  <span>+ Upload</span>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Referencing send modal */}
      {referencingOpen && (
        <ReferencingModal
          lead={lead}
          selectedKeys={referencingSelectedKeys}
          setSelectedKeys={setReferencingSelectedKeys}
          preview={referencingPreview}
          message={referencingMessage}
          setMessage={setReferencingMessage}
          subject={referencingSubject}
          setSubject={setReferencingSubject}
          result={referencingResult}
          loading={referencingLoading}
          onPreview={runPreview}
          onConfirm={confirmSend}
          onClose={() => setReferencingOpen(false)}
        />
      )}

      {/* Convert modal */}
      {convertOpen && (
        <ConvertModal
          lead={lead}
          checklist={checklist}
          properties={allProps}
          suggestedPropId={suggestedPropId}
          onClose={() => setConvertOpen(false)}
          onConverted={async () => {
            setConvertOpen(false);
            await onLeadRefresh();
          }}
        />
      )}

      {/* Step-36 soft-check modal */}
      {softCheckKey && (
        <SoftCheckModal
          lead={lead}
          itemKey={softCheckKey}
          item={(checklist && checklist[softCheckKey]) || {}}
          onClose={() => setSoftCheckKey(null)}
          onChanged={async () => { await loadChecklist(); await loadReadiness(); }}
        />
      )}
    </section>
  );
}

function ReferencingModal({ lead, selectedKeys, setSelectedKeys, preview, message, setMessage, subject, setSubject, result, loading, onPreview, onConfirm, onClose }) {
  const toggle = (key) => {
    setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  return (
    <div className="lead-modal-backdrop" onClick={() => !loading && onClose()}>
      <div className="lead-modal" onClick={e => e.stopPropagation()} data-testid="referencing-modal">
        <div className="lead-modal-header">
          <h3>Send Referencing Link</h3>
          <button className="lead-modal-close" onClick={onClose} data-testid="referencing-close">×</button>
        </div>
        <div className="lead-modal-body">
          {!preview ? (
            <>
              <p className="lead-hint">Select the checks to include in the message. The applicant will receive a link to your referencing provider.</p>
              <div className="ref-checks">
                {CHECKLIST_META.map(m => (
                  <label key={m.key} className="ref-check">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(m.key)}
                      onChange={() => toggle(m.key)}
                      data-testid={`ref-chk-${m.key}`}
                    />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="lead-row">
                <span className="lead-label">Channel</span>
                <span data-testid="ref-channel">{preview.channel === 'email' ? 'Email' : 'WhatsApp'}</span>
              </div>
              <div className="lead-row">
                <span className="lead-label">Recipient</span>
                <span data-testid="ref-recipient">{preview.recipient}</span>
              </div>
              {preview.channel === 'email' && (
                <div className="lead-modal-field">
                  <label>Subject</label>
                  <input type="text" value={subject} onChange={e => setSubject(e.target.value)} data-testid="ref-subject" />
                </div>
              )}
              <div className="lead-modal-field">
                <label>Message preview — review and edit before confirming</label>
                <textarea rows={8} value={message} onChange={e => setMessage(e.target.value)} data-testid="ref-message" />
              </div>
              {result && (
                result.error ? (
                  <div className="invite-result invite-error" data-testid="ref-result">Send failed: {result.error}</div>
                ) : result.sent ? (
                  <div className="invite-result invite-ok" data-testid="ref-result">Sent to {result.recipient} via {result.channel}.</div>
                ) : (
                  <div className="invite-result invite-warn" data-testid="ref-result">Logged, but delivery could not be confirmed — check your channel configuration.</div>
                )
              )}
            </>
          )}
        </div>
        <div className="lead-modal-footer">
          <button className="btn-secondary btn-sm" onClick={onClose} disabled={loading} data-testid="ref-cancel">Cancel</button>
          {!preview ? (
            <button className="btn-primary btn-sm" onClick={onPreview} disabled={loading || selectedKeys.length === 0} data-testid="ref-preview">
              {loading ? 'Preparing...' : 'Preview Message'}
            </button>
          ) : result?.sent ? (
            <button className="btn-primary btn-sm" onClick={onClose} data-testid="ref-done">Done</button>
          ) : (
            <button className="btn-primary btn-sm" onClick={onConfirm} disabled={loading || !message.trim()} data-testid="ref-confirm">
              {loading ? 'Sending...' : 'Confirm & Send'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConvertModal({ lead, checklist, properties, suggestedPropId, onClose, onConverted }) {
  const [form, setForm] = useState({
    property_id: suggestedPropId || lead.matched_property_id || '',
    start_date: '',
    rent_amount: '',
    rent_frequency: 'monthly',
    rent_due_day: '',
    deposit_amount: '',
    deposit_scheme: '',
    deposit_scheme_other: '',
    send_welcome: true,
    // 2026-05-29 — Compliance Scanner Q&A (required for new tenancies).
    smoke_alarms_per_storey: null,
    combustion_appliances_present: null,
    co_alarms_in_combustion_rooms: null,
    licensing_required: null,
    licensing_type: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const canContact = !!(lead.phone || lead.email);

  const schemeLabel = form.deposit_scheme === 'other'
    ? (form.deposit_scheme_other || '').trim()
    : form.deposit_scheme;

  const allFilled = !!(
    form.property_id &&
    form.start_date &&
    form.rent_amount && parseFloat(form.rent_amount) > 0 &&
    form.rent_frequency &&
    form.rent_due_day && parseInt(form.rent_due_day, 10) >= 1 && parseInt(form.rent_due_day, 10) <= 31 &&
    form.deposit_amount !== '' && parseFloat(form.deposit_amount) >= 0 &&
    schemeLabel &&
    // Compliance Q&A — all four base questions must be answered.
    // If combustion=true, co_alarms_in_combustion_rooms also required.
    // If licensing_required=true, licensing_type also required.
    form.smoke_alarms_per_storey !== null &&
    form.combustion_appliances_present !== null &&
    (form.combustion_appliances_present === false ||
     form.co_alarms_in_combustion_rooms !== null) &&
    form.licensing_required !== null &&
    (form.licensing_required === false || form.licensing_type)
  );

  const selectedProp = properties.find(p => p.property_id === form.property_id);
  const selectedHasActive = selectedProp && selectedProp.has_active_tenancy;

  const submit = async () => {
    setError(null);
    if (!canContact) {
      setError('A phone number or email address is required to create a tenant record.');
      return;
    }
    if (!allFilled) { setError('Please complete every required field before confirming.'); return; }
    if (selectedHasActive) {
      setError('This property already has an active tenancy. Please select a different property or end the existing tenancy first.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/convert`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({
          property_id: form.property_id,
          start_date: form.start_date,
          rent_amount: parseFloat(form.rent_amount),
          rent_frequency: form.rent_frequency,
          rent_due_day: parseInt(form.rent_due_day, 10),
          deposit_amount: parseFloat(form.deposit_amount),
          deposit_scheme: schemeLabel || null,
          send_welcome: !!form.send_welcome,
          // 2026-05-29 — Compliance Scanner Q&A
          smoke_alarms_per_storey: form.smoke_alarms_per_storey,
          combustion_appliances_present: form.combustion_appliances_present,
          co_alarms_in_combustion_rooms: form.co_alarms_in_combustion_rooms,
          licensing_required: form.licensing_required,
          licensing_type: form.licensing_type || null,
        }),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) { setError(d.detail || 'Conversion failed'); return; }
      setResult(d);
      // Short pause so the agent sees the success state before the modal closes.
      setTimeout(() => onConverted(), 800);
    } catch (e) {
      setError(e.message);
    } finally { setSubmitting(false); }
  };

  const fmtAvailability = (s) => (s || 'occupied').replace('_', ' ');

  return (
    <div className="lead-modal-backdrop" onClick={() => !submitting && onClose()}>
      <div className="lead-modal" onClick={e => e.stopPropagation()} data-testid="convert-modal">
        <div className="lead-modal-header">
          <h3>Convert to Tenant</h3>
          <button className="lead-modal-close" onClick={onClose} data-testid="convert-close">×</button>
        </div>
        <div className="lead-modal-body">
          {/* Pre-populated fields from the lead */}
          <div className="cv-prefill" data-testid="cv-prefill">
            <div className="cv-prefill-title">Pre-populated from this lead</div>
            <div className="cv-prefill-grid">
              <div><span className="lead-label">First name</span><strong data-testid="cv-prefill-first">{lead.first_name || '—'}</strong></div>
              <div><span className="lead-label">Last name</span><strong data-testid="cv-prefill-last">{lead.last_name || '—'}</strong></div>
              <div><span className="lead-label">Email</span><strong data-testid="cv-prefill-email">{lead.email || '—'}</strong></div>
              <div><span className="lead-label">Phone (E.164)</span><strong data-testid="cv-prefill-phone">{lead.phone || '—'}</strong></div>
            </div>
            {!canContact && (
              <div className="invite-result invite-error" data-testid="cv-no-contact">
                A phone number or email address is required to create a tenant record.
              </div>
            )}
          </div>

          <div className="lead-modal-field">
            <label>Property *</label>
            <select value={form.property_id} onChange={set('property_id')} data-testid="cv-property">
              <option value="">— Select property —</option>
              {properties.map(p => {
                const label = `${p.address_line1}, ${p.postcode} — ${fmtAvailability(p.availability_status)}${p.has_active_tenancy ? ' (active tenancy)' : ''}`;
                return (
                  <option key={p.property_id} value={p.property_id} disabled={p.has_active_tenancy}>
                    {label}
                  </option>
                );
              })}
            </select>
            {suggestedPropId && form.property_id === suggestedPropId && (
              <div className="lead-hint" data-testid="cv-suggested-hint">
                Pre-selected from the lead's property of interest — please confirm this is correct.
              </div>
            )}
            {selectedHasActive && (
              <div className="invite-result invite-error" data-testid="cv-active-tenancy-warning">
                This property already has an active tenancy. Please select a different property or end the existing tenancy first.
              </div>
            )}
          </div>

          <div className="convert-row">
            <div className="lead-modal-field">
              <label>Tenancy start date *</label>
              <input type="date" value={form.start_date} onChange={set('start_date')} data-testid="cv-start-date" />
            </div>
            <div className="lead-modal-field">
              <label>Rent frequency *</label>
              <select value={form.rent_frequency} onChange={set('rent_frequency')} data-testid="cv-rent-freq">
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
          </div>
          <div className="convert-row">
            <div className="lead-modal-field">
              <label>Rent amount (£) *</label>
              <input type="number" step="0.01" min="0" value={form.rent_amount} onChange={set('rent_amount')} data-testid="cv-rent-amount" />
            </div>
            <div className="lead-modal-field">
              <label>Rent due day (1-31) *</label>
              <input type="number" min="1" max="31" value={form.rent_due_day} onChange={set('rent_due_day')} data-testid="cv-rent-day" />
            </div>
          </div>
          <div className="convert-row">
            <div className="lead-modal-field">
              <label>Deposit amount (£) *</label>
              <input type="number" step="0.01" min="0" value={form.deposit_amount} onChange={set('deposit_amount')} data-testid="cv-deposit-amount" />
            </div>
            <div className="lead-modal-field">
              <label>Deposit scheme *</label>
              <select value={form.deposit_scheme} onChange={set('deposit_scheme')} data-testid="cv-deposit-scheme">
                <option value="">— Select scheme —</option>
                <option value="TDS">TDS</option>
                <option value="DPS">DPS</option>
                <option value="MyDeposits">MyDeposits</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          {form.deposit_scheme === 'other' && (
            <div className="lead-modal-field">
              <label>Scheme name *</label>
              <input type="text" value={form.deposit_scheme_other} onChange={set('deposit_scheme_other')}
                     placeholder="e.g. Custom deposit scheme name"
                     data-testid="cv-deposit-scheme-other" />
            </div>
          )}

          {/* 2026-05-29 — Compliance Scanner Q&A (smoke / CO / licensing).
              Mandatory for new tenancies per RRA 2026. */}
          <div style={{ marginTop: '1rem', padding: '0.85rem',
                        border: '1px solid #e2e8f0', borderRadius: 8,
                        background: '#f8fafc' }}
               data-testid="cv-compliance-qa">
            <div style={{ fontWeight: 700, color: '#0f172a',
                          marginBottom: '0.5rem', fontSize: '0.92rem' }}>
              Compliance — required for new tenancies
            </div>
            <YesNoField
              label="Are smoke alarms installed on every storey used as living accommodation?"
              value={form.smoke_alarms_per_storey}
              onChange={(v) => setForm(f => ({ ...f, smoke_alarms_per_storey: v }))}
              testid="cv-smoke"
            />
            <YesNoField
              label="Are there any fixed combustion appliances (boiler, gas fire, log burner) in any living room? (Exclude gas cookers)"
              value={form.combustion_appliances_present}
              onChange={(v) => setForm(f => ({ ...f,
                combustion_appliances_present: v,
                co_alarms_in_combustion_rooms: v ? f.co_alarms_in_combustion_rooms : null,
              }))}
              testid="cv-combustion"
            />
            {form.combustion_appliances_present === true && (
              <YesNoField
                label="Is a carbon monoxide alarm installed in each room with a combustion appliance?"
                value={form.co_alarms_in_combustion_rooms}
                onChange={(v) => setForm(f => ({ ...f, co_alarms_in_combustion_rooms: v }))}
                testid="cv-co"
              />
            )}
            <YesNoField
              label="Is this property subject to mandatory HMO licensing, selective licensing, or additional licensing?"
              value={form.licensing_required}
              onChange={(v) => setForm(f => ({ ...f,
                licensing_required: v,
                licensing_type: v ? f.licensing_type : '',
              }))}
              testid="cv-licensing"
            />
            {form.licensing_required === true && (
              <div className="lead-modal-field" style={{ marginTop: '0.5rem' }}>
                <label>Licence type *</label>
                <select value={form.licensing_type}
                        onChange={set('licensing_type')}
                        data-testid="cv-licensing-type">
                  <option value="">— Select type —</option>
                  <option value="mandatory_hmo">Mandatory HMO</option>
                  <option value="selective">Selective</option>
                  <option value="additional">Additional</option>
                </select>
                <div style={{ fontSize: '0.78rem', color: '#475569',
                              marginTop: '0.3rem' }}>
                  You can upload the licence document or application evidence
                  from the Property Detail page after the tenancy is created.
                </div>
              </div>
            )}
          </div>

          <label className="cv-welcome-toggle">
            <input type="checkbox" checked={form.send_welcome} onChange={set('send_welcome')} data-testid="cv-send-welcome" />
            <span>Send welcome message to the new tenant (subject to your autonomy mode)</span>
          </label>

          {error && <div className="invite-result invite-error" data-testid="cv-error">{error}</div>}
          {result && (
            <div className="invite-result invite-success" data-testid="cv-success">
              Tenancy created. {result.welcome_sent_immediately
                ? 'Welcome message sent automatically.'
                : (result.welcome_pending_message_id ? 'Welcome message queued for your approval.' : '')}
            </div>
          )}
          {result && result.compliance_scan && (
            <div data-testid="cv-compliance-result" style={{
              marginTop: '0.75rem',
              padding: '0.85rem',
              borderRadius: 8,
              background: result.compliance_scan.overall_status === 'red'
                ? '#fef2f2'
                : result.compliance_scan.overall_status === 'amber'
                  ? '#fef3c7'
                  : '#ecfdf5',
              border: '1px solid ' + (
                result.compliance_scan.overall_status === 'red'
                  ? '#fecaca'
                  : result.compliance_scan.overall_status === 'amber'
                    ? '#fde68a'
                    : '#a7f3d0'),
              color: '#0f172a',
              fontSize: '0.85rem',
            }}>
              <strong>Compliance scan: {result.compliance_scan.overall_status}.</strong>{' '}
              {result.compliance_scan.items.filter(i => i.status === 'red').length} critical,{' '}
              {result.compliance_scan.items.filter(i => i.status === 'amber').length} warnings.
              {' '}Open the property's Compliance tab to review and resolve.
            </div>
          )}
        </div>
        <div className="lead-modal-footer">
          <button
            style={{ background: '#e2e8f0', color: '#0f172a' }}
            className="btn-sm"
            onClick={onClose}
            disabled={submitting}
            data-testid="cv-cancel">Cancel</button>
          <button
            style={{
              background: (allFilled && canContact && !selectedHasActive && !submitting) ? '#10b981' : '#94a3b8',
              color: '#fff',
              cursor: (allFilled && canContact && !selectedHasActive && !submitting) ? 'pointer' : 'not-allowed',
            }}
            className="btn-sm"
            onClick={submit}
            disabled={!allFilled || !canContact || !!selectedHasActive || submitting}
            data-testid="cv-confirm">
            {submitting ? 'Converting...' : 'Confirm and Create Tenancy'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
 * Soft-Check Modal (Step 36)
 * One modal handles the full flow for a single checklist item:
 *   preview → agent-confirmed send → awaiting → review → extract →
 *   agent edits → mark passed/failed.
 * ===================================================================== */

const CONFIDENCE_STYLE = {
  high:   { background: '#d1fae5', color: '#065f46', label: 'High' },
  medium: { background: '#fef3c7', color: '#78350f', label: 'Medium — Please verify' },
  low:    { background: '#fee2e2', color: '#7f1d1d', label: 'Low — Please check and correct' },
};

function SoftCheckModal({ lead, itemKey, item, onClose, onChanged }) {
  const soft = item.soft_check || {};
  const initialView =
    soft.state === 'response_received' || soft.state === 'confirmed'
      ? 'review'
      : soft.state === 'awaiting_response'
      ? 'awaiting'
      : 'preview';

  const [view, setView] = useState(initialView);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [landlordContact, setLandlordContact] = useState({ name: '', email: '', phone: '' });
  const [extraText, setExtraText] = useState((soft.extra_text) || '');
  const [extracted, setExtracted] = useState(soft.extraction || null);
  const [summaryFields, setSummaryFields] = useState([]);
  const [corrected, setCorrected] = useState(null); // agent-edited copy of extracted values
  const [notes, setNotes] = useState(item.notes || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);

  const runPreview = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const body = itemKey === 'previous_landlord_reference' && (landlordContact.email || landlordContact.phone)
        ? { landlord_contact: landlordContact }
        : {};
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/checklist/${itemKey}/softcheck/preview`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify(body),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) { setError(d.detail || 'Preview failed'); return; }
      setPreview(d);
      setMessage(d.message || '');
      setSubject(d.subject || '');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [itemKey, lead._id, landlordContact]);

  useEffect(() => {
    if (view === 'preview' && !preview && !(itemKey === 'previous_landlord_reference' && !landlordContact.email && !landlordContact.phone)) {
      runPreview();
    }
  }, [view, preview, runPreview, itemKey, landlordContact]);

  const confirmSend = async () => {
    setError(null); setLoading(true);
    try {
      const body = {
        message,
        subject: subject || null,
        landlord_contact:
          itemKey === 'previous_landlord_reference' && (landlordContact.email || landlordContact.phone)
            ? landlordContact
            : null,
      };
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/checklist/${itemKey}/softcheck/send`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify(body),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) { setError(d.detail || 'Send failed'); return; }
      setSentInfo(d);
      setView('awaiting');
      await onChanged();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const runExtract = async () => {
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/checklist/${itemKey}/softcheck/extract`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify({ extra_text: extraText || null }),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) { setError(d.detail || 'Extraction failed'); return; }
      setExtracted(d.extraction || {});
      setSummaryFields(d.summary_fields || []);
      setCorrected((d.extraction && { ...d.extraction }) || {});
      setView('review');
      await onChanged();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (view === 'review' && !extracted && soft.extraction) {
      setExtracted(soft.extraction);
      setCorrected({ ...soft.extraction });
    }
  }, [view, extracted, soft.extraction]);

  const confirmOutcome = async (outcome) => {
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/${lead._id}/checklist/${itemKey}/softcheck/confirm`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({
          confirmed_fields: corrected || {},
          outcome,
          agent_notes: notes || null,
        }),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) { setError(d.detail || 'Confirm failed'); return; }
      await onChanged();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const title = {
    aml_check: 'AML Check',
    right_to_rent_check: 'Right to Rent Check',
    employer_reference: 'Employment Verification',
    credit_check: 'Income Analysis',
    previous_landlord_reference: 'Landlord Reference',
  }[itemKey] || 'Soft Check';

  return (
    <div className="lead-modal-backdrop" onClick={() => !loading && onClose()}>
      <div className="lead-modal lead-modal-wide" onClick={e => e.stopPropagation()} data-testid="softcheck-modal">
        <div className="lead-modal-header">
          <h3>{title}</h3>
          <button className="lead-modal-close" onClick={onClose} data-testid="softcheck-close">×</button>
        </div>
        <div className="lead-modal-body">
          {/* ----- Preview step ----- */}
          {view === 'preview' && (
            <>
              {itemKey === 'previous_landlord_reference' && (
                <div className="lead-modal-field">
                  <label>Previous landlord contact — needed to send the reference request</label>
                  <div className="convert-row">
                    <input type="text" placeholder="Landlord name" value={landlordContact.name} onChange={e => setLandlordContact(v => ({ ...v, name: e.target.value }))} data-testid="sc-landlord-name" />
                    <input type="email" placeholder="Landlord email" value={landlordContact.email} onChange={e => setLandlordContact(v => ({ ...v, email: e.target.value }))} data-testid="sc-landlord-email" />
                  </div>
                  <input type="text" placeholder="OR WhatsApp number" value={landlordContact.phone} onChange={e => setLandlordContact(v => ({ ...v, phone: e.target.value }))} data-testid="sc-landlord-phone" style={{marginTop: '0.5rem'}} />
                  {!preview && (landlordContact.email || landlordContact.phone) && (
                    <button onClick={runPreview} disabled={loading} className="btn-primary btn-sm" style={{ background: '#0073EA', color: '#fff', marginTop: '0.5rem' }} data-testid="sc-landlord-build-preview">
                      {loading ? 'Building...' : 'Build Preview'}
                    </button>
                  )}
                </div>
              )}
              {preview && (
                <>
                  <div className="lead-row">
                    <span className="lead-label">Channel</span>
                    <span data-testid="sc-channel">{preview.channel === 'email' ? 'Email' : 'WhatsApp'}</span>
                  </div>
                  <div className="lead-row">
                    <span className="lead-label">Recipient</span>
                    <span data-testid="sc-recipient">{preview.recipient}</span>
                  </div>
                  {preview.channel === 'email' && (
                    <div className="lead-modal-field">
                      <label>Subject</label>
                      <input type="text" value={subject} onChange={e => setSubject(e.target.value)} data-testid="sc-subject" />
                    </div>
                  )}
                  <div className="lead-modal-field">
                    <label>Message — review and edit before confirming</label>
                    <textarea rows={8} value={message} onChange={e => setMessage(e.target.value)} data-testid="sc-message" />
                  </div>
                </>
              )}
              {error && <div className="invite-result invite-error" data-testid="sc-error">{error}</div>}
            </>
          )}

          {/* ----- Awaiting step ----- */}
          {view === 'awaiting' && (
            <>
              <div className="sc-awaiting-banner" data-testid="sc-awaiting-banner">
                Sent {sentInfo ? `to ${sentInfo.recipient}` : ''}. Awaiting response.
              </div>
              <p className="lead-hint">
                When the {itemKey === 'previous_landlord_reference' ? 'landlord replies' : 'applicant replies'},
                upload any documents they send using the existing "+ Upload" slot on the checklist row.
                You can also paste the reply text below. Then click Run Extraction to process everything with AI.
              </p>
              <div className="lead-modal-field">
                <label>Reply text (optional — paste the message body here)</label>
                <textarea rows={6} value={extraText} onChange={e => setExtraText(e.target.value)} data-testid="sc-extra-text" />
              </div>
              {error && <div className="invite-result invite-error">{error}</div>}
            </>
          )}

          {/* ----- Review step ----- */}
          {view === 'review' && (
            <>
              <p className="lead-hint">
                Claude extracted the following from the uploaded document(s) and any reply text.
                Review each field, correct inaccuracies, then mark the check passed or failed.
              </p>
              <SoftCheckReviewFields
                extracted={extracted || {}}
                corrected={corrected || {}}
                setCorrected={setCorrected}
                summaryFields={summaryFields.length ? summaryFields : Object.keys(extracted || {}).filter(k => !k.endsWith('_confidence'))}
              />
              <div className="lead-modal-field" style={{marginTop: '0.75rem'}}>
                <label>Agent notes (optional)</label>
                <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} data-testid="sc-notes" />
              </div>
              {error && <div className="invite-result invite-error">{error}</div>}
            </>
          )}
        </div>
        <div className="lead-modal-footer">
          <button className="btn-secondary btn-sm" onClick={onClose} disabled={loading} data-testid="sc-cancel">Cancel</button>

          {view === 'preview' && preview && (
            <button
              onClick={confirmSend}
              disabled={loading || !message.trim()}
              style={{ background: '#0073EA', color: '#fff', padding: '0.4rem 0.85rem', borderRadius: '5px', border: 'none', fontWeight: 600, cursor: 'pointer' }}
              data-testid="sc-confirm-send"
            >
              {loading ? 'Sending...' : 'Confirm & Send'}
            </button>
          )}

          {view === 'awaiting' && (
            <button
              onClick={runExtract}
              disabled={loading}
              style={{ background: '#0073EA', color: '#fff', padding: '0.4rem 0.85rem', borderRadius: '5px', border: 'none', fontWeight: 600, cursor: 'pointer' }}
              data-testid="sc-run-extract"
            >
              {loading ? 'Extracting...' : 'Run Extraction'}
            </button>
          )}

          {view === 'review' && (
            <>
              <button
                onClick={() => confirmOutcome('failed')}
                disabled={loading}
                style={{ background: '#dc2626', color: '#fff', padding: '0.4rem 0.85rem', borderRadius: '5px', border: 'none', fontWeight: 600, cursor: 'pointer' }}
                data-testid="sc-mark-failed"
              >
                Mark Failed
              </button>
              <button
                onClick={() => confirmOutcome('passed')}
                disabled={loading}
                style={{ background: '#10b981', color: '#fff', padding: '0.4rem 0.85rem', borderRadius: '5px', border: 'none', fontWeight: 600, cursor: 'pointer' }}
                data-testid="sc-mark-passed"
              >
                Mark Passed
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SoftCheckReviewFields({ extracted, corrected, setCorrected, summaryFields }) {
  if (!extracted || extracted.parse_error) {
    return (
      <div className="invite-result invite-error">
        Extraction could not be parsed. Please review the raw response and enter the values manually via notes.
        {extracted?.raw_response && (
          <pre style={{whiteSpace: 'pre-wrap', marginTop: '0.5rem', fontSize: '0.75rem'}}>{extracted.raw_response}</pre>
        )}
      </div>
    );
  }
  const render = (key) => {
    if (key.endsWith('_confidence')) return null;
    const val = corrected[key];
    const conf = (extracted[`${key}_confidence`] || 'low').toLowerCase();
    const cstyle = CONFIDENCE_STYLE[conf] || CONFIDENCE_STYLE.low;
    // Render array fields as JSON for the agent to edit; primitive fields
    // as a text input.
    const isArray = Array.isArray(val);
    const isObject = val !== null && typeof val === 'object' && !isArray;
    return (
      <div key={key} className="sc-field-row" data-testid={`sc-field-${key}`}>
        <div className="sc-field-label">
          {key.replace(/_/g, ' ')}
          <span className="sc-conf-pill" style={{ background: cstyle.background, color: cstyle.color }} data-testid={`sc-conf-${key}`}>
            {cstyle.label}
          </span>
        </div>
        {(isArray || isObject) ? (
          <textarea
            rows={3}
            value={JSON.stringify(val ?? (isArray ? [] : {}), null, 2)}
            onChange={e => {
              try {
                const parsed = JSON.parse(e.target.value);
                setCorrected(c => ({ ...c, [key]: parsed }));
              } catch {
                // keep last good value
              }
            }}
          />
        ) : (
          <input
            type="text"
            value={val == null ? '' : String(val)}
            onChange={e => setCorrected(c => ({ ...c, [key]: e.target.value }))}
          />
        )}
      </div>
    );
  };
  const keys = summaryFields && summaryFields.length
    ? summaryFields
    : Object.keys(extracted).filter(k => !k.endsWith('_confidence'));
  return (
    <div className="sc-fields">
      {keys.map(render)}
    </div>
  );
}
